import React from 'react';
import { MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ViewType } from '../hooks/useViewState';
import type { IapScope } from '../ICSConstants';
import type { ActionColumnId } from './actionColumnModel';
import { ActionColumnMenuItems } from './ActionColumnMenuItems';

const VIEW_OPTIONS: { label: string; value: ViewType }[] = [
  { label: 'List', value: 'table' },
  { label: 'Board', value: 'kanban' },
  { label: 'Timeline', value: 'timeline' },
];

function isViewType(value: string): value is ViewType {
  return value === 'table' || value === 'kanban' || value === 'timeline';
}

type ActionsViewMenuProps = {
  view: ViewType;
  onViewChange: (view: ViewType) => void;
  scope: IapScope;
  hiddenColumns: readonly string[];
  onToggleColumn: (id: ActionColumnId) => void;
  onResetColumns: () => void;
};

/** Kebab for layout radios and Actions-table column checkboxes. */
export const ActionsViewMenu: React.FC<ActionsViewMenuProps> = ({
  view,
  onViewChange,
  scope,
  hiddenColumns,
  onToggleColumn,
  onResetColumns,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label="Task view options"
            data-tour="hwcg-tasks-view"
          />
        }
      >
        <MoreVertical className="tw:h-4 tw:w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="dark tw:w-52">
        <DropdownMenuRadioGroup
          value={view}
          onValueChange={(next) => {
            if (isViewType(next)) onViewChange(next);
          }}
        >
          <DropdownMenuLabel>Layout</DropdownMenuLabel>
          {VIEW_OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuLabel>Columns</DropdownMenuLabel>
          <ActionColumnMenuItems
            scope={scope}
            hidden={hiddenColumns}
            onToggle={onToggleColumn}
            onReset={onResetColumns}
          />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
